import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './PlayersRole.css';

const roleIcons = {
  'Batter': '🏏',
  'Fast Bowler': '🔥',
  'Spin Bowler': '🌀',
  'Batting Allrounder': '⚡',
  'Bowling Allrounder': '💫',
  'WicketKeeper': '🧤',
  'WicketKeeper Batter': '🎯'
};

const skillColors = {
  'Budding': { bg: 'linear-gradient(135deg, #059669, #10b981)', text: '#ecfdf5' },
  'Budding+': { bg: 'linear-gradient(135deg, #2563eb, #3b82f6)', text: '#eff6ff' },
  'Rising': { bg: 'linear-gradient(135deg, #7c3aed, #8b5cf6)', text: '#f5f3ff' },
  'Shining': { bg: 'linear-gradient(135deg, #d97706, #f59e0b)', text: '#fffbeb' },
  'Elite': { bg: 'linear-gradient(135deg, #dc2626, #ef4444)', text: '#fef2f2' },
  'Elite+': { bg: 'linear-gradient(135deg, #0f172a, #1e293b)', text: '#f1f5f9' },
};

const PlayersRole = () => {
  const [players, setPlayers] = useState([]);
  const [skillLevels, setSkillLevels] = useState([]);
  const [filterSkillLevel, setFilterSkillLevel] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editedPlayers, setEditedPlayers] = useState({});
  const [savingPlayers, setSavingPlayers] = useState({});
  const [expandedCard, setExpandedCard] = useState(null);

  const primaryRoleOptions = [
    'Batter',
    'Fast Bowler',
    'Spin Bowler',
    'Batting Allrounder',
    'Bowling Allrounder',
    'WicketKeeper',
    'WicketKeeper Batter'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [playersResponse, skillLevelsResponse] = await Promise.all([
        api.get('/api/kids'),
        api.get('/api/skill-levels')
      ]);
      
      const playersData = playersResponse.data.kids || [];
      const activeKids = playersData.filter(kid => kid.is_approved && kid.is_active);
      
      setPlayers(activeKids);
      setSkillLevels(skillLevelsResponse.data.skill_levels || []);
      
    } catch (error) {
      console.error('Error fetching players:', error);
      setError(error.response?.data?.error || 'Failed to load players data');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleUpdate = async (playerId) => {
    try {
      setError('');
      setSavingPlayers(prev => ({ ...prev, [playerId]: true }));
      
      const player = players.find(p => p.id === playerId);
      const updateData = {
        primary_role: player.primary_role,
        batting_order: player.batting_order,
        bowling_order: player.bowling_order
      };
      
      await api.put(`/api/kids/${playerId}/role`, updateData);
      
      setEditedPlayers(prev => {
        const updated = { ...prev };
        delete updated[playerId];
        return updated;
      });
      
      setSuccess(`Player role updated and parent notified!`);
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error updating player role:', error);
      setError(error.response?.data?.error || 'Failed to update player role');
    } finally {
      setSavingPlayers(prev => {
        const updated = { ...prev };
        delete updated[playerId];
        return updated;
      });
    }
  };

  const handleExportExcel = () => {
    const headers = ['Player Name', 'Age', 'Skill', 'Cric Club ID', 'USA Cricket ID', 'Primary Role', 'Batting Order', 'Bowling Order'];
    const csvRows = [headers.join(',')];
    
    filteredPlayers.forEach(player => {
      const row = [
        `"${player.first_name} ${player.last_name}"`,
        getPlayerAge(player.date_of_birth),
        `"${player.skill_category_name || 'No Category'}"`,
        `"${player.cric_club_id || ''}"`,
        `"${player.usacid || ''}"`,
        `"${player.primary_role || ''}"`,
        `"${player.batting_order || ''}"`,
        `"${player.bowling_order || ''}"`
      ];
      csvRows.push(row.join(','));
    });
    
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `players_role_report_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setSuccess('Excel file downloaded successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleEmailReport = async () => {
    try {
      setLoading(true);
      setError('');
      
      const reportData = filteredPlayers.map(player => ({
        name: `${player.first_name} ${player.last_name}`,
        age: getPlayerAge(player.date_of_birth),
        skill: player.skill_category_name || 'No Category',
        cric_club_id: player.cric_club_id || '',
        usacid: player.usacid || '',
        primary_role: player.primary_role || '',
        batting_order: player.batting_order || '',
        bowling_order: player.bowling_order || ''
      }));
      
      await api.post('/api/players/email-report', { players: reportData });
      
      setSuccess('Report emailed successfully to admin!');
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (error) {
      console.error('Error emailing report:', error);
      setError(error.response?.data?.error || 'Failed to email report');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (playerId, field, value) => {
    setPlayers(prevPlayers => 
      prevPlayers.map(player => 
        player.id === playerId 
          ? { ...player, [field]: value }
          : player
      )
    );
    setEditedPlayers(prev => ({ ...prev, [playerId]: true }));
  };

  const hasUnsavedChanges = (playerId) => {
    return editedPlayers[playerId] === true;
  };

  const getPlayerAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    const age = Math.floor((new Date() - new Date(dateOfBirth)) / (365.25 * 24 * 60 * 60 * 1000));
    return age;
  };

  const getInitials = (firstName, lastName) => {
    return `${(firstName || '')[0] || ''}${(lastName || '')[0] || ''}`.toUpperCase();
  };

  const getSkillStyle = (skillName) => {
    return skillColors[skillName] || { bg: 'linear-gradient(135deg, #475569, #64748b)', text: '#f1f5f9' };
  };

  const filteredPlayers = players.filter(player => {
    const matchesSkillLevel = filterSkillLevel === 'all' || 
      player.skill_category_id === parseInt(filterSkillLevel);
    const matchesSearch = searchTerm === '' || 
      `${player.first_name} ${player.last_name}`.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSkillLevel && matchesSearch;
  });

  const assignedCount = filteredPlayers.filter(p => p.primary_role).length;
  const unassignedCount = filteredPlayers.length - assignedCount;

  if (loading) {
    return (
      <div className="pr-loading">
        <div className="pr-loading-spinner">
          <div className="pr-spinner-ring"></div>
          <span className="pr-spinner-icon">🏏</span>
        </div>
        <p className="pr-loading-text">Loading players...</p>
      </div>
    );
  }

  return (
    <div className="pr-page">
      {/* Hero Banner */}
      <div className="pr-hero">
        <div className="pr-hero-shine" />
        <div className="pr-hero-content">
          <div className="pr-hero-left">
            <div className="pr-hero-badge">Role Management</div>
            <h2 className="pr-hero-title">Players Role</h2>
          </div>
          <div className="pr-hero-mid">
            <div className="pr-hero-stat">
              <span className="pr-hero-stat-num">{filteredPlayers.length}</span>
              <span className="pr-hero-stat-label">Total</span>
            </div>
            <div className="pr-hero-stat-divider" />
            <div className="pr-hero-stat">
              <span className="pr-hero-stat-num">{assignedCount}</span>
              <span className="pr-hero-stat-label">Assigned</span>
            </div>
            <div className="pr-hero-stat-divider" />
            <div className="pr-hero-stat">
              <span className="pr-hero-stat-num">{unassignedCount}</span>
              <span className="pr-hero-stat-label">Unassigned</span>
            </div>
          </div>
          <div className="pr-hero-actions">
            <button onClick={handleExportExcel} className="pr-hero-btn pr-hero-btn-export">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M14 10v3a1 1 0 01-1 1H3a1 1 0 01-1-1v-3M11 5L8 2 5 5M8 2v10"/></svg>
              Export
            </button>
            <button onClick={handleEmailReport} className="pr-hero-btn pr-hero-btn-email">
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M2 4l6 4 6-4M2 4a1 1 0 011-1h10a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1V4z"/></svg>
              Email
            </button>
          </div>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="pr-alert pr-alert-error">
          <span>{error}</span>
          <button className="pr-alert-close" onClick={() => setError('')}>&times;</button>
        </div>
      )}
      {success && (
        <div className="pr-alert pr-alert-success">
          <span>{success}</span>
          <button className="pr-alert-close" onClick={() => setSuccess('')}>&times;</button>
        </div>
      )}

      {/* Search Bar */}
      <div className="pr-search-wrap">
        <svg className="pr-search-icon" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd"/></svg>
        <input
          type="text"
          placeholder="Search players..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pr-search-input"
        />
        {searchTerm && (
          <button onClick={() => setSearchTerm('')} className="pr-search-clear">✕</button>
        )}
      </div>

      {/* Filter Chips */}
      <div className="pr-filters">
        <button
          className={`pr-chip ${filterSkillLevel === 'all' ? 'active' : ''}`}
          onClick={() => setFilterSkillLevel('all')}
        >
          <span className="pr-chip-icon">👥</span>All
        </button>
        {skillLevels.map(level => (
          <button
            key={level.id}
            className={`pr-chip ${filterSkillLevel === String(level.id) ? 'active' : ''}`}
            onClick={() => setFilterSkillLevel(String(level.id))}
          >
            <span className="pr-chip-icon">🏏</span>{level.name}
            <span className="pr-chip-count">{players.filter(p => p.skill_category_id === level.id).length}</span>
          </button>
        ))}
      </div>

      {/* Player Cards List */}
      <div className="pr-list">
        {filteredPlayers.length > 0 ? filteredPlayers.map(player => {
          const skillStyle = getSkillStyle(player.skill_category_name);
          const isExpanded = expandedCard === player.id;
          const isEdited = hasUnsavedChanges(player.id);
          return (
            <div
              key={player.id}
              className={`pr-card ${isEdited ? 'pr-card-edited' : ''}`}
            >
              <div className="pr-card-main" onClick={() => setExpandedCard(isExpanded ? null : player.id)}>
                <div className="pr-avatar" style={{ background: player.profile_pic_url ? 'none' : skillStyle.bg }}>
                  {player.profile_pic_url ? (
                    <img src={player.profile_pic_url} alt="" />
                  ) : (
                    getInitials(player.first_name, player.last_name)
                  )}
                </div>
                <div className="pr-card-info">
                  <div className="pr-card-name">{player.first_name} {player.last_name}</div>
                  <div className="pr-card-meta">
                    <span className="pr-pill" style={{ background: skillStyle.bg, color: skillStyle.text }}>
                      {player.skill_category_name || 'Unassigned'}
                    </span>
                    <span className="pr-card-detail">Age {getPlayerAge(player.date_of_birth)}</span>
                    {player.cric_club_id && <span className="pr-pill pr-pill-id">Club: {player.cric_club_id}</span>}
                    {player.usacid && <span className="pr-pill pr-pill-id">USA: {player.usacid}</span>}
                  </div>
                </div>
                <div className="pr-card-right">
                  {player.primary_role && (
                    <span className="pr-role-badge">
                      {roleIcons[player.primary_role]} {player.primary_role}
                    </span>
                  )}
                  {!player.primary_role && (
                    <span className="pr-status pr-status-unassigned">Unassigned</span>
                  )}
                  <svg className={`pr-chevron ${isExpanded ? 'pr-chevron-open' : ''}`} width="20" height="20" viewBox="0 0 20 20" fill="none"><path d="M6 8l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
              </div>
              {isExpanded && (
                <div className="pr-card-expand">
                  <div className="pr-detail-grid">
                    <div className="pr-detail"><span className="pr-detail-lbl">Skill Level</span><span>{player.skill_category_name || '—'}</span></div>
                    <div className="pr-detail"><span className="pr-detail-lbl">Age</span><span>{getPlayerAge(player.date_of_birth)}</span></div>
                    <div className="pr-detail"><span className="pr-detail-lbl">Cric Club ID</span><span>{player.cric_club_id || '—'}</span></div>
                    <div className="pr-detail"><span className="pr-detail-lbl">USA Cricket ID</span><span>{player.usacid || '—'}</span></div>
                  </div>
                  <div className="pr-edit-section">
                    <div className="pr-edit-field">
                      <label>Primary Role</label>
                      <select
                        value={player.primary_role || ''}
                        onChange={(e) => handleInputChange(player.id, 'primary_role', e.target.value)}
                        className="pr-select"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="">Select Role</option>
                        {primaryRoleOptions.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>
                    <div className="pr-edit-row">
                      <div className="pr-edit-field">
                        <label>Batting Order</label>
                        <input
                          type="text"
                          value={player.batting_order || ''}
                          onChange={(e) => handleInputChange(player.id, 'batting_order', e.target.value)}
                          placeholder="#"
                          className="pr-order-input"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                      <div className="pr-edit-field">
                        <label>Bowling Order</label>
                        <input
                          type="text"
                          value={player.bowling_order || ''}
                          onChange={(e) => handleInputChange(player.id, 'bowling_order', e.target.value)}
                          placeholder="#"
                          className="pr-order-input"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleRoleUpdate(player.id); }}
                      disabled={!isEdited || savingPlayers[player.id]}
                      className={`pr-save-btn ${isEdited ? 'pr-save-btn-active' : ''}`}
                    >
                      {savingPlayers[player.id] ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        }) : (
          <div className="pr-empty">
            <span className="pr-empty-icon">🔍</span>
            <p>No players found{searchTerm ? ` matching "${searchTerm}"` : ''}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="pr-footer">
        Showing {filteredPlayers.length} of {players.length} players
      </div>
    </div>
  );
};

export default PlayersRole;
